/**
 * Feature Flag Context
 *
 * Provides feature flag state to the entire application via React Context.
 * Automatically fetches flags on mount and provides a refetch function.
 *
 * Usage:
 * ```
 * import { useFeatureFlags } from 'datastage/context/FeatureFlagContext';
 *
 * function MyComponent() {
 *   const { isHyperlaneEnabled, loading, refetch } = useFeatureFlags();
 *
 *   if (loading) return <LoadingSpinner />;
 *
 *   return (
 *     <div>
 *       {isHyperlaneEnabled ? 'Hyperlane Mode' : 'Legacy Mode'}
 *     </div>
 *   );
 * }
 * ```
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { featureFlagService } from 'datastage/services/featureFlagService';

const FeatureFlagContext = createContext({
    isHyperlaneEnabled: false,
    loading: true,
    error: null,
    refetch: () => {},
});

export const useFeatureFlags = () => {
    const context = useContext(FeatureFlagContext);
    if (!context) {
        throw new Error('useFeatureFlags must be used within FeatureFlagProvider');
    }
    return context;
};

export const FeatureFlagProvider = ({ children }) => {
    const [isHyperlaneEnabled, setIsHyperlaneEnabled] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchFlags = useCallback(async () => {
        setLoading(true);
        try {
            const enabled = await featureFlagService.isHyperlaneEnabled();
            setIsHyperlaneEnabled(enabled);
            setError(null);
        } catch (err) {
            console.error('[FeatureFlagContext] Error fetching flags:', err);
            setError(err.message);
            // Default to legacy on error
            setIsHyperlaneEnabled(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchFlags();
    }, [fetchFlags]);

    const value = {
        isHyperlaneEnabled,
        loading,
        error,
        refetch: fetchFlags,
    };

    return (
        <FeatureFlagContext.Provider value={value}>
            {children}
        </FeatureFlagContext.Provider>
    );
};
